import { STOCK, STOCK_CHOICES, baseSpeed } from "../model/rollingstock";
import { PERSO_BOUNDS, stockFor, type PersoParams } from "../model/run";
import { useApp } from "../state/store";

/** Champs du matériel personnalisé : clé SI, libellé, unité d'affichage, facteur SI → affiché, pas. */
const CHAMPS: { cle: keyof PersoParams; label: string; unite: string; k: number; pas: number }[] = [
  { cle: "mass_kg", label: "Masse en charge", unite: "t", k: 1 / 1000, pas: 5 },
  { cle: "power_w", label: "Puissance", unite: "kW", k: 1 / 1000, pas: 100 },
  { cle: "a0", label: "Accélération au démarrage", unite: "m/s²", k: 1, pas: 0.05 },
  { cle: "d", label: "Décélération de service", unite: "m/s²", k: 1, pas: 0.05 },
  { cle: "vmax_ms", label: "Vitesse maximale", unite: "km/h", k: 3.6, pas: 5 },
];

/** Matériel roulant (preset ou personnalisé), temps d'arrêt, et ce que le modèle prend en compte. */
export function Reglages() {
  const settings = useApp((s) => s.scenario.settings);
  const setSettings = useApp((s) => s.setSettings);
  const stock = stockFor(settings);

  const choisir = (v: string) => {
    if (v === "perso") {
      const { mass_kg, power_w, a0, d, vmax_ms } = stock;
      setSettings({ stock: "perso", perso: { mass_kg, power_w, a0, d, vmax_ms } });
    } else {
      setSettings({ stock: v as typeof settings.stock });
    }
  };
  const perso = settings.perso ?? stock;

  return (
    <section className="reglages">
      <h2>Réglages</h2>
      <label>
        Matériel roulant
        <select value={settings.stock} onChange={(e) => choisir(e.target.value)}>
          {STOCK_CHOICES.map((id) => (
            <option key={id} value={id}>
              {STOCK[id].label} — {STOCK[id].family}
            </option>
          ))}
          <option value="perso">Personnalisé…</option>
        </select>
        <small>
          {(stock.mass_kg / 1000).toFixed(0)} t · {(stock.power_w / 1000).toFixed(0)} kW · {stock.a0} m/s² ·{" "}
          {(stock.vmax_ms * 3.6).toFixed(0)} km/h · vitesse de base {(baseSpeed(stock) * 3.6).toFixed(0)} km/h
          {stock.note && ` · ${stock.note}`}
        </small>
      </label>
      {settings.stock === "perso" && (
        <div className="perso">
          {CHAMPS.map((c) => (
            <label key={c.cle}>
              {c.label} ({c.unite})
              <input
                type="number"
                inputMode="decimal"
                step={c.pas}
                min={PERSO_BOUNDS[c.cle][0] * c.k}
                max={PERSO_BOUNDS[c.cle][1] * c.k}
                value={Number((perso[c.cle] * c.k).toFixed(2))}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (!Number.isFinite(v)) return;
                  setSettings({ perso: { ...perso, [c.cle]: v / c.k } });
                }}
              />
            </label>
          ))}
          <small>
            La vitesse de base η·P/(m·a0) marque la fin de l'effort constant : au-delà, c'est la puissance qui limite.
          </small>
        </div>
      )}
      <label>
        Temps d'arrêt en gare : <strong>{settings.tarr} s</strong>
        <input
          type="range"
          min={15}
          max={90}
          step={5}
          value={settings.tarr}
          onChange={(e) => setSettings({ tarr: Number(e.target.value) })}
        />
      </label>
      <label className="case">
        <input type="checkbox" checked={settings.useVmax} onChange={(e) => setSettings({ useVmax: e.target.checked })} />
        Limitations de vitesse par section (courbes du tracé)
      </label>
      <label className="case">
        <input type="checkbox" checked={settings.useGrade} onChange={(e) => setSettings({ useGrade: e.target.checked })} />
        Profil de pente (rail, ≤ 3,5 %)
      </label>
    </section>
  );
}
